import {Box, Tooltip, Typography} from '@mui/material'
import type {FC} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import type {UserContextProps} from '@app/containers/App/UserProvider'
import {useUserContext} from '@app/containers/App/UserProvider'
import {lastSeenFunction} from '@app/util-functions'
import {ConnectivityStatusEnum} from '@app/shared/interfaces/common/healthStatus'

interface LineStatusChipProps {
  connectivityStatus: ConnectivityStatusEnum | string | null
  displayLabel?: boolean
  bold?: boolean
  lastValue?: boolean
  tooltipText?: string
  tooltipKey?: string
  lastValueTimestamp?: Date | null
}

const LineStatusChip: FC<LineStatusChipProps> = ({
  connectivityStatus,
  bold,
  tooltipText,
  tooltipKey,
  displayLabel = true,
  lastValue = false,
  lastValueTimestamp,
}) => {
  const [t] = useCustomTranslation('lines_overview')
  const [tCommon] = useCustomTranslation('common')
  const {currentUser} = useUserContext() as UserContextProps

  const getChipLabel = (): string => {
    switch (connectivityStatus?.toLowerCase()) {
      case ConnectivityStatusEnum.CONNECTED:
        return 'Connected'
      case ConnectivityStatusEnum.ONLINE:
        return 'Online'
      case ConnectivityStatusEnum.DISCONNECTED:
        return 'Disconnected'
      case ConnectivityStatusEnum.OFFLINE:
        return 'Offline'
      case ConnectivityStatusEnum.NO_DATA_15MIN:
        return t('no_data_15min')
      case ConnectivityStatusEnum.NO_DATA:
        return ''
      case 'unknown':
        return 'Unknown'
      default:
        return 'Unknown'
    }
  }

  const getChipColor = (): string => {
    switch (connectivityStatus?.toLowerCase()) {
      case ConnectivityStatusEnum.CONNECTED:
        return 'success.main'
      case ConnectivityStatusEnum.ONLINE:
        return 'success.main'
      case ConnectivityStatusEnum.DISCONNECTED:
        return 'error.main'
      case ConnectivityStatusEnum.OFFLINE:
        return 'error.main'
      case ConnectivityStatusEnum.NO_DATA_15MIN:
        return 'primary.main'
      case ConnectivityStatusEnum.NO_DATA:
        return ''
      case 'unknown':
        return 'action.disabled'
      default:
        return 'action.disabled'
    }
  }

  const getTooltipTitle = (): string => {
    if (tooltipText) {
      return tooltipText
    }

    switch (connectivityStatus?.toLowerCase()) {
      case ConnectivityStatusEnum.ONLINE:
        return t('online_tooltip')
      case ConnectivityStatusEnum.CONNECTED:
        return t(`online_tooltip_${tooltipKey || 'default'}`)
      case ConnectivityStatusEnum.OFFLINE:
        return t('offline_tooltip')
      case ConnectivityStatusEnum.DISCONNECTED:
        return t(`offline_tooltip_${tooltipKey || 'default'}`)
      case ConnectivityStatusEnum.NO_DATA_15MIN:
        return t('no_data_15min_tooltip')
      case ConnectivityStatusEnum.NO_DATA:
        return ''
      case 'unknown':
        return 'Unknown'
      default:
        return 'Unknown'
    }
  }

  const getTooltipEnding = (): string => {
    if (lastValue) {
      return lastValueTimestamp
        ? `${tCommon('signals.last_datapoint_received')} ${lastSeenFunction(lastValueTimestamp, currentUser)}`
        : tCommon('signals.no_latency_value')
    }
    return ''
  }

  const getCompleteTooltipTitle = (): string => {
    return `${getTooltipTitle()}    ${getTooltipEnding()}`.trim()
  }

  return connectivityStatus ? (
    <Tooltip arrow title={getCompleteTooltipTitle()}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mr: 1,
        }}
      >
        <Box
          sx={{
            height: displayLabel ? '10px' : '15px',
            width: displayLabel ? '10px' : '15px',
            backgroundColor: getChipColor(),
            mr: 1,
            borderRadius: '25px',
          }}
        />
        <Typography
          variant={bold ? 'body2' : 'caption'}
          sx={{color: bold ? 'text.primary' : 'text.disabled', fontWeight: bold ? 600 : 400}}
        >
          {displayLabel ? getChipLabel() : null}
        </Typography>
      </Box>
    </Tooltip>
  ) : null
}

export default LineStatusChip
